import { num } from '../copy';
import { type ICancellationImpact, type ICancellationImpactSummary } from '../types';

/**
 * The signature line: what a cancellation costs, phrased as consequence.
 *
 * Three states per fact, never two: a number, "not captured" (the server looked and has
 * nothing — register null, or the legacy 0 that means "nobody entered it"), and "—" (we do
 * not know, because the impact route is absent or has not answered). Collapsing the last two
 * would tell a candidate a unit has no voters when we simply have not asked.
 */

export type ImplicationTone = 'fact' | 'unknown' | 'alert';

export interface IImplicationPart {
	key: 'register' | 'expected' | 'results';
	text: string;
	tone: ImplicationTone;
}

/** `undefined` impact = not available/not loaded; `null` expected = estimate has no figure. */
export const implicationParts = (
	impact: ICancellationImpact | undefined,
	expectedVotes: number | null | undefined
): IImplicationPart[] => {
	const parts: IImplicationPart[] = [];

	if (!impact) {
		parts.push({ key: 'register', text: '— registered voters', tone: 'unknown' });
	} else if (impact.registeredVoters === null || impact.registeredVoters === undefined || impact.registeredVoters <= 0) {
		parts.push({ key: 'register', text: 'Register not captured', tone: 'unknown' });
	} else {
		parts.push({
			key: 'register',
			text: `${num(impact.registeredVoters)} registered ${impact.registeredVoters === 1 ? 'voter' : 'voters'}`,
			tone: 'fact',
		});
	}

	if (expectedVotes === undefined) {
		parts.push({ key: 'expected', text: '— expected votes', tone: 'unknown' });
	} else if (expectedVotes === null) {
		parts.push({ key: 'expected', text: 'No vote estimate for this unit', tone: 'unknown' });
	} else {
		parts.push({ key: 'expected', text: `~${num(Math.round(expectedVotes))} expected votes for us (estimate)`, tone: 'fact' });
	}

	if (impact?.anyResultSubmitted === true) {
		parts.push({ key: 'results', text: 'Result already submitted', tone: 'alert' });
	} else if (impact?.anyResultSubmitted === false) {
		parts.push({ key: 'results', text: 'No result submitted', tone: 'fact' });
	} else {
		parts.push({ key: 'results', text: '— results', tone: 'unknown' });
	}

	return parts;
};

export interface ITotalsFigure {
	/** null = unknown ("—"). */
	value: number | null;
	/** Shown under the figure — the basis, e.g. "at least · 3 of 5 captured". */
	note: string | null;
}

export interface ICancellationTotals {
	units: ITotalsFigure;
	registered: ITotalsFigure;
	expected: ITotalsFigure;
	withResults: ITotalsFigure;
	unreviewed: number | null;
	basis: 'server' | 'client';
}

interface IUnitFigures {
	code: string;
	registeredVoters: number | null | undefined;
	expectedVotes: number | null | undefined;
}

/**
 * The totals strip. The server's impact summary wins whenever it answered; otherwise the
 * figures are summed in the browser from the per-unit estimate and labelled "at least n of
 * N captured", because a client sum over a partial register is a floor, not a total.
 *
 * Expected votes are ALWAYS a client sum (the server deliberately leaves them to the
 * Strong Hold estimate) and are always labelled an estimate.
 */
export const buildTotals = (args: {
	summary: ICancellationImpactSummary | undefined;
	activeCodes: string[];
	totalCount: number | undefined;
	unitFigures: Map<string, IUnitFigures> | null;
	unreviewedCount: number | null;
}): ICancellationTotals => {
	const { summary, activeCodes, totalCount, unitFigures, unreviewedCount } = args;
	const n = summary?.units ?? totalCount ?? activeCodes.length;

	let expectedSum = 0;
	let expectedKnown = 0;
	let registerSum = 0;
	let registerKnown = 0;
	if (unitFigures) {
		for (const code of activeCodes) {
			const unit = unitFigures.get(code);
			if (!unit) continue;
			if (typeof unit.expectedVotes === 'number') {
				expectedSum += unit.expectedVotes;
				expectedKnown += 1;
			}
			if (typeof unit.registeredVoters === 'number' && unit.registeredVoters > 0) {
				registerSum += unit.registeredVoters;
				registerKnown += 1;
			}
		}
	}
	const sampled = activeCodes.length;
	const expected: ITotalsFigure =
		!unitFigures || expectedKnown === 0
			? { value: null, note: unitFigures ? 'No estimate for these units' : 'Estimate unavailable' }
			: {
					value: Math.round(expectedSum),
					note: expectedKnown < n ? `estimate · at least, ${num(expectedKnown)} of ${num(n)} estimated` : 'estimate',
				};

	if (summary) {
		const captured = summary.registeredCapturedUnits;
		const registerValue = summary.registeredVoters !== null && summary.registeredVoters > 0 ? summary.registeredVoters : null;
		const registered: ITotalsFigure =
			registerValue === null
				? { value: null, note: 'Register not captured' }
				: {
						value: registerValue,
						note:
							captured !== undefined && captured < summary.units
								? `at least · ${num(captured)} of ${num(summary.units)} captured`
								: null,
					};
		return {
			units: { value: summary.units, note: null },
			registered,
			expected,
			withResults: {
				value: summary.unitsWithResults ?? null,
				note: summary.unitsWithResults === undefined ? 'Not reported by this server' : null,
			},
			unreviewed: summary.unitsUnreviewed ?? unreviewedCount,
			basis: 'server',
		};
	}

	return {
		units: { value: n, note: null },
		registered:
			registerKnown === 0
				? { value: null, note: unitFigures ? 'Register not captured' : 'Register unavailable' }
				: { value: registerSum, note: `at least · ${num(registerKnown)} of ${num(Math.max(n, sampled))} captured` },
		expected,
		withResults: { value: null, note: 'Not reported by this server' },
		unreviewed: unreviewedCount,
		basis: 'client',
	};
};
